import React, { memo, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { useDispatch } from "react-redux"
import { useNavigate } from "react-router-dom"
import { styled } from "@mui/material/styles"
import Stack from "@mui/material/Stack"
import Typography from "@mui/material/Typography"
import { IRT, ITEM } from "constants/index"
import { PATH_INFOS } from "utils/navigatorUtils"
import { formatThousands } from "utils/numberUtils"
import { updateCurrentItemIndex, updateCurrentItemInfo, updateWishListViewMode } from "appRedux/slices/wishListSlice"
import { useAppSelector } from "appRedux/hooks"
import { getPublicAwarePath, getStaticAssetUrl } from "appRedux/baseQuery"
import LazyImage from "components/atoms/LazyImage"
import { Badge } from "components/atoms/Badge"
import WishItemStatusBox from "components/molecules/WishItemStatusBox"

type WishItemSummaryCardProps = {
  index: number;
  itemInfo: WishItem;
}

export default memo(function WishItemSummaryCard({ index, itemInfo }: WishItemSummaryCardProps) {
  const currentPageInfo = useAppSelector((state) => state.global.currentPageInfo)

  const imageContainerRef = useRef<HTMLDivElement | null>(null)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { t } = useTranslation()

  const shouldRenderPriceSection = useMemo(() => {
    return itemInfo.price && itemInfo.currency
  }, [itemInfo.price && itemInfo.currency])

  function handleWishItemCardClicked() {
    dispatch(updateCurrentItemInfo({ ...itemInfo, wishlists: [] }))
    navigateToSelectedItem()
  }

  function navigateToSelectedItem() {
    if ([PATH_INFOS.viewList.path].includes(currentPageInfo?.path)) {
      dispatch(updateCurrentItemIndex(index))
      dispatch(updateWishListViewMode(ITEM))
    } else {
      const handle = currentPageInfo?.dynamicPathParts?.handle
      navigate(getPublicAwarePath(`/user/${handle}/items`))
    }
  }

  function renderItemImage() {
    if (itemInfo.image instanceof File) {
      const fileReader = new FileReader()

      fileReader.onload = () => {
        if (!imageContainerRef.current) return
        imageContainerRef.current.style.backgroundImage = `url(${fileReader.result as string})`
      }

      fileReader.readAsDataURL(itemInfo.image)
    } else {
      return <LazyImage
        src={typeof itemInfo.image === "string" ? getStaticAssetUrl(itemInfo.image) : null}
        alt={itemInfo.name}
        style={{ objectFit: "contain" }}
      />
    }
  }

  function renderPrice() {
    const currency = itemInfo.currency
    if (currency !== IRT) return String(itemInfo.price)
    return formatThousands(itemInfo.price)
  }

  return (
    <Card onClick={handleWishItemCardClicked}>
      <ImageContainer ref={imageContainerRef}>
        {renderItemImage()}
      </ImageContainer>
      <ItemInfoWrapper>
        <Stack width="100%" justifyContent="space-between" gap={2}>
          <Name>{itemInfo.name}</Name>
          {shouldRenderPriceSection && <Stack direction="row" alignItems="center" justifyContent="flex-start" gap="2px">
              <Price className={itemInfo.currency === IRT ? "farsi-digits" : ""}>{renderPrice()}</Price>
              <Currency>{t(`currency.${itemInfo.currency}`)}</Currency>
          </Stack>}
        </Stack>
        <StatusBoxWrapper>
          {itemInfo.cameTrue && <Badge
              label={t("items.purchased")}
              color="success"
              onClick={() => {
              }}
          />}
          <WishItemStatusBox
            commentsCount={itemInfo.commentCount}
            likesCount={itemInfo.likeCount}
            saveCount={itemInfo.saveCount}
            shareCount={itemInfo.shareCount}
          />
        </StatusBoxWrapper>
      </ItemInfoWrapper>
    </Card>
  )
})

const Card = styled(Stack)(({ theme }) => ({
  flexDirection: "row",
  justifyContent: "space-between",
  gap: "12px",
  backgroundColor: "#FFFFFF",
  borderRadius: "8px",
  padding: "8px",
  boxShadow: `0 0 4px 0 ${theme.palette.gray["6"]}`
}))

const ImageContainer = styled(Stack)(({ theme }) => ({
  width: "66px",
  height: "88px",
  borderRadius: "8px",
  border: `1px solid ${theme.palette.gray["8"]}`,
  backgroundColor: theme?.palette.gray["4"],
  position: "relative",
  alignItems: "center",
  justifyContent: "center",
  flexShrink: 0,
  overflow: "hidden",
  backgroundSize: "cover"
}))

const ItemInfoWrapper = styled(Stack)(({ theme }) => ({
  width: "100%",
  justifyContent: "space-between",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis"
}))

const Name = styled(Typography)(({ theme }) => ({
  fontSize: "1.4rem",
  fontWeight: 500,
  color: theme.palette.gray["15"],
  width: "100%",
  overflow: "hidden",
  whiteSpace: "nowrap",
  textOverflow: "ellipsis"
}))

const Price = styled(Typography)(({ theme }) => ({
  fontSize: "1.4rem",
  fontWeight: 400,
  color: theme.palette.gray["15"]
}))

const Currency = styled(Typography)(({ theme }) => ({
  fontSize: "1rem",
  fontWeight: 400,
  color: theme.palette.gray["11"]
}))

const StatusBoxWrapper = styled(Stack)(({ theme }) => ({
  width: "100%",
  flexDirection: "row",
  justifyContent: "space-between",
  alignItems: "center",
  marginLeft: "auto",
  marginRight: "8px"
}))
